import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Modal, Field, TextInput, TextArea, Toggle, PrimaryButton } from "@/components/admin/ui";

const empty = { title: "", description: "", image_url: "", button_text: "", link: "", active: true, order: 0 };

export default function ManageBanners() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const { data: items } = useQuery({ queryKey: ["admin-banners"], queryFn: async () => (await api.get("/admin/banners")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-banners"] });
    qc.invalidateQueries({ queryKey: ["banners"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const save = useMutation({
    mutationFn: async (data) => editing ? api.put(`/admin/banners/${editing}`, data) : api.post("/admin/banners", data),
    onSuccess: () => { invalidate(); setModal(false); toast.success(editing ? "Banner updated" : "Banner added"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/banners/${id}`),
    onSuccess: () => { invalidate(); toast.success("Banner deleted"); },
  });

  const openAdd = () => { setForm({ ...empty, order: (items?.length || 0) }); setEditing(null); setModal(true); };
  const openEdit = (b) => { setForm(b); setEditing(b.id); setModal(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Banners / Images</h1>
          <p className="mt-1 text-slate-400">Promotional banners shown on the home page carousel.</p>
        </div>
        <PrimaryButton onClick={openAdd} testid="add-banner-btn"><Plus className="h-5 w-5" /> Add</PrimaryButton>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {(items || []).map((b) => (
          <div key={b.id} className="card-surface overflow-hidden rounded-xl" data-testid={`admin-banner-${b.id}`}>
            <div className="relative h-40 bg-obsidian">
              {b.image_url ? <img src={resolveImage(b.image_url)} alt="" className="h-full w-full object-cover" />
                : <div className="flex h-full items-center justify-center text-slate-600"><ImageIcon className="h-8 w-8" /></div>}
              <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-semibold ${b.active ? "bg-whatsapp text-white" : "bg-slate-700 text-slate-300"}`}>{b.active ? "Active" : "Inactive"}</span>
            </div>
            <div className="p-4">
              <p className="font-bold text-slate-100">{b.title || "Untitled"}</p>
              <p className="line-clamp-1 text-sm text-slate-400">{b.description}</p>
              <div className="mt-3 flex justify-end gap-1">
                <button onClick={() => openEdit(b)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`edit-banner-${b.id}`}><Pencil className="h-5 w-5" /></button>
                <button onClick={() => { if (window.confirm("Delete this banner?")) del.mutate(b.id); }} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-banner-${b.id}`}><Trash2 className="h-5 w-5" /></button>
              </div>
            </div>
          </div>
        ))}
        {items?.length === 0 && <p className="col-span-full py-12 text-center text-slate-500">No banners yet.</p>}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Banner" : "Add Banner"} testid="banner-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
          <ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} label="Banner Image" testid="banner-image" />
          <Field label="Title (optional)">
            <TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} testid="banner-title-input" />
          </Field>
          <Field label="Description (optional)">
            <TextArea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} testid="banner-description-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Button Text (optional)">
              <TextInput value={form.button_text} onChange={(e) => setForm((f) => ({ ...f, button_text: e.target.value }))} placeholder="Download App" testid="banner-button-input" />
            </Field>
            <Field label="Link (optional)">
              <TextInput value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} placeholder="https:// or /resellers" testid="banner-link-input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 items-center gap-3">
            <Field label="Display Order">
              <TextInput type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} testid="banner-order-input" />
            </Field>
            <div className="pt-6">
              <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label={form.active ? "Active" : "Inactive"} testid="banner-active-toggle" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={save.isPending} testid="save-banner-btn">{save.isPending ? "Saving..." : "Save"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
